import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Typography, makeStyles, Paper } from '@material-ui/core';
import { AuthenticateUser, FetchUserData, ReAuthenticate, LogOut, GetDashboardCounts, GetDashboardReferringUrl, GetDashboardData, GetChatHistory, GetPDFData, GetDashboardReports, GetLocation, ForgotPassword, SendResetCode, ResetPassword, SaveFieldsForPartner, GetAllFieldsForPartner } from './services/DashboardServiceList';

import PageHeader from './components/Header';
import PageFooter from './components/PageFooter';
import * as FileSaver from 'file-saver';

// Lazy load components
const Login = lazy(() => import('./components/Login'));
const SendEmailPage = lazy(() => import('./pages/reset-password/SendEmailPage'));
const VerifyCodePage = lazy(() => import('./pages/reset-password/VerifyCodePage'));
const ConfirmNewPasswordPage = lazy(() => import('./pages/reset-password/ConfirmNewPasswordPage'));
const StatsCard = lazy(() => import('./components/StatsCard'));
const GridComponent = lazy(() => import('./components/Grid'));

interface Address {
    streetLine1: string;
    streetLine2: string;
    city: string;
    state: string;
    county: string;
    postalCode: string;
    country: string;
}

interface PartnerMapping {
    partnerId: string;
    partnerName: string;
    isPartnerAdmin: boolean;
}

interface User {
    id: string;
    userStatus: string;
    createdOn: string;
    firstName: string;
    lastName: string;
    fullName: string;
    mobileNumber: string;
    email: string;
    password: string;
    address: Address;
    allowAddressEntry: boolean;
    agentTitle: string;
    avatarUrl: string;
    timeZone: string;
    partnerMappings: PartnerMapping[];
}

// ENG-877: Per-field config the partner has set up, returned by
// GET /GetAllFieldsForPartner. The Configure Dashboard Fields modal is
// driven off the filtered Dashboard subset of these records.
interface PartnerField {
    fieldName: string;       // key in the dashboard row data (e.g. 'petOwnerName')
    fieldKeyLabel: string;   // display label
    fieldId: string;         // backend identifier used in save payloads
    isEnable: boolean;       // partner's saved default for visibility
    order: number;           // display order
    isOptional: boolean;     // true → user can toggle; false → mandatory
    applicationType: 'Dashboard' | 'Report' | string;
}

// ENG-877: The new GetPartnerChatHistoriesByPartnerConfig endpoint returns a
// dynamic field set per partner config (the response schema is a free-form
// dictionary of strings, not a fixed shape). The known fields below are kept
// as a documented baseline because the Grid still renders them when present,
// and the index signature lets any extra per-partner custom fields flow through
// without TypeScript errors.
interface DashboardDataItem {
    sessionId?: string;
    sessionNumber?: number;
    firstName?: string;
    lastName?: string;
    createdOn?: string;
    petOwnerName?: string;
    petOwnerId?: string;
    petName?: string;
    speciesName?: string;
    breedName?: string;
    openingQuery?: string;
    vetNotes?: string;
    chatType?: string;
    chatStatus?: string;
    referringUrl?: string;
    location?: string;
    // Catch-all for partner-specific custom fields returned by the new API.
    [key: string]: any;
}

interface ChatHistoryData {
    speciesName: string;
    petBreed: string;
    petGender: string;
    petDateOfBirth: string;
    isSpayedOrNeutered: boolean;
    lastAssignedToAgent: string | null;
    participants: Participant[];
    posts: Post[];
    files: any[];
    feedbackRating: number | null;
    feedbackComment: string | null;
    petOwnerRecommendations: string | null;
    name: string;
    feedbackDate: string | null;
    intakeAnswers: any[];
    featureId: string;
    id: string;
    createdOn: string;
    petId: string;
    petName: string;
    intakeQuestion: string;
    commType: string;
    hasRating: boolean;
    source: string;
    subject: string;
    isPetOwnerAnonymized: boolean;
    petOwnerName: string;
    senderDisplayName: string;
    senderFullName: string | null;
    senderPhoneNumber: string;
    petAvatarUrl: string | null;
    textPreview: string | null;
    sentOn: string;
    entryType: string;
    entryId: string;
    rating: number | null;
    senderParticipantType: string | null;
    messageStatus: string | null;
}

interface Participant {
    id: string;
    userId: string;
    type: string;
    salutation: string | null;
    firstName: string;
    lastName: string;
    fullName: string;
    avatarUrl: string | null;
    agentDisplayName: string | null;
    agentTitle: string | null;
}

interface Post {
    timestamp: string;
    participantId: string;
    text: string | null;
    files: any[];
    postType: string;
    newQueue: string | null;
}

type ServerError = string | null;

const useStyles = makeStyles((theme) => ({
    appContainer: {
        alignItems: 'center',
        backgroundColor: '#063056',
        backgroundSize: 'cover',
        height: '100vh',
        maxWidth: '2000px',
        margin: 'auto',
    },
    error: {
        color: 'rgb(235, 34, 77)',
    },
}));


const Dashboard: React.FC = () => {
    const classes = useStyles();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [pdfDownloading, setPDFDownloading] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [todaysSummaryLoading, setTodaysSummaryLoading] = useState(false);
    const [referringUrlLoading, setReferringLoading] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [isLoggedIn, setLoggedIn] = useState(false);
    const [isResettingPassword, setIsResettingPassword] = useState(false);
    const [error, setError] = useState('');
    const [dashboardData, setDashboardData] = useState<DashboardDataItem[]>([]);
    const [locationsCheckboxList, setLocationsCheckboxList] = useState<[]>([]);

    // ENG-877: The full field config the partner has set up, fetched via
    // GET /GetAllFieldsForPartner whenever the selected partner changes.
    // Filtered to applicationType === 'Dashboard' before being handed to
    // the Grid (the modal that consumes it is the Dashboard's own).
    const [partnerFieldsConfig, setPartnerFieldsConfig] = useState<PartnerField[] | null>(null);
    // ENG-877: Same idea for Reports — filtered to applicationType === 'Report'.
    // Drives the new Fields section inside the existing Reports modal.
    const [partnerReportFieldsConfig, setPartnerReportFieldsConfig] = useState<PartnerField[] | null>(null);

    const [chatData, setChatData] = useState<ChatHistoryData | null>(null);
    const [selectedCheckboxData, setSelectedCheckboxData] = useState<string[]>([]);
    const [serverError, setServerError] = useState<ServerError>(null);

    const [resetEmail, setResetEmail] = useState('');
    const [resetCode, setResetCode] = useState('');

    const [resetEmailSuccess, setResetEmailSuccess] = useState(false);
    const [resetCodeSuccess, setResetCodeSuccess] = useState(false);

    const [todaysSummary, setTodaysSummary] = useState<any>({
        newVisitorsToday: 0,
        repeatVisitorsToday: 0,
        totalVeraChatsToday: 0,
        requestedLiveChatToday: 0
    });

    const [summary, setSummary] = useState<any>({
        newVisitorsInDateRange: 0,
        repeatVisitorsInDateRange: 0,
        totalVeraChatsInDateRange: 0,
        requestedLiveChatInDateRange: 0
    });

    const [referringUrls, setReferringUrls] = useState<any[]>([]);

    // ─── FIX 1: Restore page number from localStorage instead of always starting at 0 ───
    // Previously: const [page, setPage] = useState<number>(0);
    // Previously: const [currentPage, setCurrentPage] = useState<number>(page);
    // The stored `skip` value is a row offset (e.g. 20), so divide by 10 to get the page index.
    const storedDashboardState: any = localStorage.getItem('dashboardState');
    const parsedDashboardState = storedDashboardState ? JSON.parse(storedDashboardState) : {};

    const storedReportDashboardState: any = localStorage.getItem('reportDataState');
    const parsedReportDashboardState = storedReportDashboardState ? JSON.parse(storedReportDashboardState) : {};

    // ─── FIX: Use helper functions so summary/referring state is always read fresh ──────────
    // Previously these were plain const values computed once — stale after any parent write.
    // Now getSummaryState()/getReferringState() always return the latest localStorage value.
    const getSummaryState = () => {
        const raw = localStorage.getItem('storedSummaryData');
        return raw ? JSON.parse(raw) : {};
    };
    const getReferringState = () => {
        const raw = localStorage.getItem('storedReferringUrlData');
        return raw ? JSON.parse(raw) : {};
    };
    const parsedSummaryState = getSummaryState();
    const parsedReferringState = getReferringState();
    const storedSummaryState = parsedSummaryState;
    const storedReferringState = parsedReferringState;
    // ─── END FIX ─────────────────────────────────────────────────────────────────────────

    const savedPage = parsedDashboardState?.skip ? Math.floor(parsedDashboardState.skip / 10) : 0;
    const [page, setPage] = useState<number>(savedPage);
    const [currentPage, setCurrentPage] = useState<number>(savedPage);
    // ─── END FIX 1 ───────────────────────────────────────────────────────────────────────

    const [rowsPerPage, setRowsPerPage] = useState<number>(10);
    const [totalCount, setTotalCount] = useState<number>(100);
    const [user, setUser] = useState<User | null>(null);

    const storedPartnerId: any = localStorage.getItem('partnerId');
    const storedPartnerName: any = localStorage.getItem('partnerName');

    // ─── FIX: Fallback uses optional chaining safely; Header sorts, App just stores ──
    const [firstPartnerId, setFirstPartnerId] = useState(storedPartnerId ? storedPartnerId : user?.partnerMappings[0]?.partnerId || '');
    const [selectedPartnerId, setSelectedPartnerId] = useState(firstPartnerId);
    const [selectedPartnerName, setSelectedPartnerName] = useState(storedPartnerName ? storedPartnerName : user?.partnerMappings[0]?.partnerName || '');
    const currentDate = new Date();
    const last3MonthStart = new Date(currentDate.getFullYear(), currentDate.getMonth() - 3, currentDate.getDate());
    const fromDate = last3MonthStart.toISOString();
    const toDate = currentDate.toISOString();

    const [logoutTimeout, setLogoutTimeout] = useState<NodeJS.Timeout | null>(null);

    const initialRequestBody = {
        skip: 0,
        take: 100,
        sorts: [{ sort: 'CreatedOn', isAscending: true }],
        partnerId: firstPartnerId,
        customStartDate: fromDate,
        customEndDate: toDate,
        searchText: '',
        isDateFilter: true,
        dateFilter: 'ThisMonth',
        chatTypes: ['All'],
        locations: ['All Locations'],
        isChatType: true,
    };

    // ─── FIX 2: Initialize currentRequestBody from localStorage if available ─────────────
    // Previously: const [currentRequestBody, setCurrentRequestBody] = useState<any>(initialRequestBody);
    // This caused all filter/date/sort state to reset to defaults on every page refresh.
    // Now we seed from the stored state, falling back to initialRequestBody only on first load.
    const [currentRequestBody, setCurrentRequestBody] = useState<any>(
        parsedDashboardState && Object.keys(parsedDashboardState).length > 0
            ? parsedDashboardState
            : initialRequestBody
    );
    // ─── END FIX 2 ───────────────────────────────────────────────────────────────────────

    const updateCurrentRequestBody = (updatedRequestBody: any) => {
        setCurrentRequestBody((previousRequestBody: any) => {
            const newRequestBody = {
                ...previousRequestBody,
                ...updatedRequestBody,
            };
            localStorage.setItem('dashboardState', JSON.stringify(newRequestBody));
            return newRequestBody;
        });
    };

    // ─── FIX 3: Rewrote handlePartnerChange to save localStorage objects atomically ──────
    // Previously, the code did repeated calls like:
    //   localStorage.setItem('dashboardState', JSON.stringify(parsedDashboardState.partnerId = partnerId));
    // This is a destructive mutation bug — it:
    //   (a) mutates parsedDashboardState in place (side effect)
    //   (b) saves only the assigned VALUE (e.g. "abc123"), not the whole object
    // The result was a corrupted localStorage key containing just a scalar string.
    // Fix: build complete new objects and write them in a single setItem call each.
    // ─── FIX: handlePartnerChange now distinguishes refresh (same partner) vs real switch ──
    // Previously it ALWAYS reset storedSummaryData/storedReferringUrlData to default dates.
    // Since it's called on every page load via useEffect, this wiped the user's saved filter.
    //
    // isPartnerSwitch=true  → real partner change, reset dates to defaults
    // isPartnerSwitch=false → page refresh with same partner, preserve stored dates
    const handlePartnerChange = (partnerId: any, partnerName: string, isPartnerSwitch: boolean = false) => {
        setSearchLoading(true);
        setDashboardData([]);

        localStorage.setItem('partnerId', partnerId);
        localStorage.setItem('partnerName', partnerName);

        const currentSummaryState = getSummaryState();
        const currentReferringState = getReferringState();
        const isSamePartner = currentSummaryState.partnerId === partnerId;

        if (isPartnerSwitch || !isSamePartner) {
            // Real partner switch — reset everything to defaults
            const resetBody = {
                ...initialRequestBody,
                partnerId,
                customStartDate: fromDate,
                customEndDate: toDate,
                dateFilter: 'ThisMonth',
                locations: ['All Locations'],
                chatTypes: ['All'],
                sorts: [{ sort: 'CreatedOn', isAscending: true }],
                skip: 0,
            };

            localStorage.setItem('dashboardState', JSON.stringify({ ...parsedDashboardState, ...resetBody }));
            localStorage.setItem('storedSummaryData', JSON.stringify({
                customStartDate: resetBody.customStartDate,
                customEndDate: resetBody.customEndDate,
                dateFilter: resetBody.dateFilter,
                partnerId,
            }));
            localStorage.setItem('storedReferringUrlData', JSON.stringify({
                customStartDate: resetBody.customStartDate,
                customEndDate: resetBody.customEndDate,
                dateFilter: resetBody.dateFilter,
                partnerId,
            }));

            setSelectedPartnerId(partnerId);
            setSelectedPartnerName(partnerName);
            updateCurrentRequestBody({ ...resetBody });

            handleLoadCountData(partnerId, resetBody.dateFilter, resetBody.customStartDate, resetBody.customEndDate);
            handleLoadReferringUrlData(partnerId, resetBody.dateFilter, resetBody.customStartDate, resetBody.customEndDate);

            fetchDashboardData(partnerId, { ...resetBody, locations: ['All Locations'], skip: 0 }, 0, rowsPerPage);
            localStorage.setItem('reportDataState', JSON.stringify({ ...parsedReportDashboardState, locations: ['All Locations'] }));
            fetchDashboardLocationsData(partnerId, { ...resetBody }, 0, rowsPerPage);

        } else {
            // Same partner (page refresh) — preserve stored date filter selections
            const dashboardResetBody = { ...parsedDashboardState, partnerId };

            localStorage.setItem('dashboardState', JSON.stringify(dashboardResetBody));
            // Only update partnerId, leave dateFilter/dates untouched
            localStorage.setItem('storedSummaryData', JSON.stringify({ ...currentSummaryState, partnerId }));
            localStorage.setItem('storedReferringUrlData', JSON.stringify({ ...currentReferringState, partnerId }));

            setSelectedPartnerId(partnerId);
            setSelectedPartnerName(partnerName);
            updateCurrentRequestBody({ ...dashboardResetBody });

            // Load data using the PRESERVED date filters, not reset defaults
            handleLoadCountData(
                partnerId,
                currentSummaryState.dateFilter || 'ThisMonth',
                currentSummaryState.customStartDate || fromDate,
                currentSummaryState.customEndDate || toDate
            );
            handleLoadReferringUrlData(
                partnerId,
                currentReferringState.dateFilter || 'ThisMonth',
                currentReferringState.customStartDate || fromDate,
                currentReferringState.customEndDate || toDate
            );

            fetchDashboardData(
                partnerId,
                { ...dashboardResetBody, skip: parsedDashboardState.skip ?? 0 },
                parsedDashboardState.skip ?? 0,
                rowsPerPage
            );
            fetchDashboardLocationsData(partnerId, { ...dashboardResetBody }, parsedDashboardState.skip ?? 0, rowsPerPage);
        }
    };
    // ─── END FIX ─────────────────────────────────────────────────────────────────────────

    const fetchDashboardData = async (newPartnerId: string | undefined, requestBody: any, pageNum: number, rowsPerPage: number) => {
        const currentPayload = { ...currentRequestBody, ...requestBody, partnerId: newPartnerId ? newPartnerId : firstPartnerId, skip: pageNum, take: rowsPerPage };

        // Update the stored payload with the current one
        localStorage.setItem('dashboardState', JSON.stringify(currentPayload));

        if (newPartnerId || firstPartnerId) {
            setSearchLoading(true);
            updateCurrentRequestBody(currentPayload);
            // ENG-1225: Send currentPayload (which forces take: rowsPerPage and
            // skip: pageNum) rather than the raw requestBody. On a partner switch
            // requestBody carries take: 100 from initialRequestBody, which made the
            // grid load all rows instead of the first page.
            await fetchDataAndUpdateUI(currentPayload);
        }
    };

    const abortControllerRef = useRef<AbortController | null>(null)

    const fetchDataAndUpdateUI = async (requestBody: any) => {
        abortControllerRef.current?.abort();
        abortControllerRef.current = new AbortController();

        setSearchLoading(true);

        const storedUser: any = localStorage.getItem('accessToken');
        const token = JSON.parse(storedUser);
        if (requestBody.partnerId !== '') {
            try {
                const response: any = await GetDashboardData(token, requestBody, abortControllerRef.current?.signal);
                if (response) {
                    setDashboardData(response.pageResults);
                    setTotalCount(response.totalCount);
                    // ─── FIX: Don't reset page to 0 on every fetch — restore from requestBody.skip
                    // so pagination position survives a page refresh. Page only resets to 0
                    // when the user explicitly changes filters, searches, sorts, or logs out.
                    const restoredPage = requestBody.skip ? Math.floor(requestBody.skip / 10) : 0;
                    setPage(restoredPage);
                    setCurrentPage(restoredPage);
                    setSearchLoading(false);
                }
            } catch (error: any) {
                if (error.status === 401) {
                    await handleReauthenticationAndRetry(token, requestBody, fetchDataAndUpdateUI);
                } else if (error.status === 400) {
                    setSearchLoading(false);
                    setDashboardData([]);
                    setChatData(null);
                } else {
                    setSearchLoading(false);
                    setDashboardData([]);
                    setChatData(null);
                }
            } finally {
                if (abortControllerRef.current?.signal.aborted) {
                    setSearchLoading(true);
                }
            }
        }
    };

    const fetchDashboardLocationsData = async (newPartnerId: string | undefined, requestBody: any, pageNum: number, rowsPerPage: number) => {
        if (newPartnerId || firstPartnerId) {
            const count = pageNum;
            updateCurrentRequestBody({ ...currentRequestBody, ...requestBody, partnerId: newPartnerId ? newPartnerId : firstPartnerId, skip: count, take: rowsPerPage });
            await fetchDataAndUpdateUIWithLocations(requestBody);
        }
    };

    const fetchDataAndUpdateUIWithLocations = async (requestBody: any) => {
        const storedUser: any = localStorage.getItem('accessToken');
        const token = JSON.parse(storedUser);
        if (requestBody.partnerId !== '') {
            try {
                const response: any = await GetLocation(token, requestBody.partnerId);
                if (response) {
                    setLocationsCheckboxList(response);
                }
            } catch (error: any) {
                console.error('Error fetching locations data:', error.status);
                if (error.status === 401) {
                    await handleReauthenticationAndRetry(token, requestBody, fetchDataAndUpdateUIWithLocations);
                } else if (error.status === 400) {
                    setLocationsCheckboxList([]);
                } else {
                    setLocationsCheckboxList([]);
                }
            }
        }
    };

    const handleReauthenticationAndRetry = async (token: string, requestBody: any, retryFunction: (requestBody: any) => Promise<void>) => {
        const refreshToken: string | null = localStorage.getItem('refreshToken');
        if (!refreshToken) {
            handleLogout();
            return;
        }
        try {
            const reAuthResponse = await ReAuthenticate(token, refreshToken);
            localStorage.setItem('accessToken', JSON.stringify(reAuthResponse.accessToken));
            localStorage.setItem('refreshToken', JSON.stringify(reAuthResponse.refreshToken));
            await retryFunction(requestBody);
        } catch (error) {
            console.error('Error reauthenticating:', error);
            handleLogout();
            return;
        }
    };

    const handleLoadChatData = async (data: any) => {
        setChatData(null);
        setModalLoading(true);
        const storedUser: any = localStorage.getItem('accessToken');
        const token = JSON.parse(storedUser);
        // ENG-877: Case-insensitive sessionId check — data endpoint may return
        // PascalCase keys (SessionId) for some partners.
        const hasSessionId = data && Object.keys(data).some(
            (k) => k.toLowerCase() === 'sessionid' && data[k]
        );
        if (hasSessionId) {
            try {
                await loadChatData(token, data);
                setModalLoading(false);
            } catch (error: any) {
                console.error('Error fetching data:', error, error.status);
                if (error.status === 401) {
                    try {
                        await retryAfterReauthentication(token, data, loadChatData);
                    } catch (reAuthError) {
                        handleLogout();
                        return;
                    } finally {
                        setModalLoading(false);
                    }
                } else {
                    setModalLoading(false);
                }
            }
        }
    };

    const loadChatData = async (token: string | null, data: any) => {
        // ENG-877: Reverted to original GetChatHistory GET endpoint —
        // sessionId + petOwnerId query params, returns rich chat detail
        // (posts, participants, intakeAnswers) that the modal renders.
        // Case-insensitive key lookup: the data endpoint may return
        // PascalCase keys (SessionId, PetOwnerId) depending on partner config.
        const pick = (row: any, key: string): string => {
            if (!row) return '';
            if (row[key] !== undefined && row[key] !== null) return String(row[key]);
            const lower = key.toLowerCase();
            for (const k of Object.keys(row)) {
                if (k.toLowerCase() === lower) return String(row[k] ?? '');
            }
            return '';
        };
        const sid = pick(data, 'sessionId');
        const pid = pick(data, 'petOwnerId');
        console.log('[GetChatHistory] sessionId:', sid, 'petOwnerId:', pid);
        const response: any = await GetChatHistory(token, sid, pid);
        setChatData(response);
    };
    // ENG-877: Persist the user's Configure Dashboard Fields modal selection
    // via POST /SaveFieldsForPartner. The Grid builds the full payload (all
    // mandatory + all optional fields with isEnable + order + label) and
    // hands it to us; we add the current partnerId, auth token, and an
    // auth-retry path consistent with the other service calls.
    const handleSaveFieldConfig = async (
        payload: Array<{
            fieldId: string;
            fieldKeyLabel: string;
            isEnable: boolean;
            order: number;
            applicationType: 'Dashboard' | 'Report';
        }>
    ) => {
        const storedUser: any = localStorage.getItem('accessToken');
        const token = JSON.parse(storedUser);
        try {
            await SaveFieldsForPartner(token, selectedPartnerId, payload);
        } catch (error: any) {
            if (error?.status === 401) {
                try {
                    await retryAfterReauthentication(token, payload, async (t: any, p: any) => {
                        await SaveFieldsForPartner(t, selectedPartnerId, p);
                    });
                } catch (reAuthError) {
                    handleLogout();
                }
            } else {
                console.error('Error saving dashboard field config:', error);
            }
        }
    };

    // ENG-877: Same shape as handleSaveFieldConfig but pins
    // applicationType = 'Report'. Called from the Reports modal Submit.
    const handleSaveReportFieldConfig = async (
        payload: Array<{
            fieldId: string;
            fieldKeyLabel: string;
            isEnable: boolean;
            order: number;
            applicationType: 'Dashboard' | 'Report';
        }>
    ) => {
        const storedUser: any = localStorage.getItem('accessToken');
        const token = JSON.parse(storedUser);
        try {
            await SaveFieldsForPartner(token, selectedPartnerId, payload);
        } catch (error: any) {
            if (error?.status === 401) {
                try {
                    await retryAfterReauthentication(token, payload, async (t: any, p: any) => {
                        await SaveFieldsForPartner(t, selectedPartnerId, p);
                    });
                } catch (reAuthError) {
                    handleLogout();
                }
            } else {
                console.error('Error saving report field config:', error);
            }
        }
    };

    // ENG-877: Fetch the partner's full field configuration. The Grid uses
    // the Dashboard subset to drive the Configure Dashboard Fields modal
    // (available fields, mandatory vs optional, default isEnable, render
    // order, and the backend fieldId used by the matching save call).
    // ENG: Guard against a redundant concurrent fetch for the same partner. On
    // login the config is requested twice (the explicit on-login call AND the
    // selectedPartnerId effect both fire), which rendered the modal fields twice
    // until a manual refresh. Skip if a fetch for this partner is already in
    // flight; cleared in finally so partner switches / post-save refetches work.
    const fetchingPartnerFieldsRef = useRef<string | null>(null);
    const fetchPartnerFieldsConfig = async (partnerId: string) => {
        if (!partnerId) return;
        if (fetchingPartnerFieldsRef.current === partnerId) return;
        fetchingPartnerFieldsRef.current = partnerId;
        const storedUser: any = localStorage.getItem('accessToken');
        const token = JSON.parse(storedUser);
        // ENG-1226: Filter by applicationType (case-insensitive). IMPORTANT: we do
        // NOT de-duplicate here — SaveFieldsForPartner walks this full set and the
        // backend rejects the save if the record count doesn't match what it
        // stored ("Records count doesn't match with system records"). Duplicate
        // fieldNames are collapsed at the DISPLAY layer instead (Grid.tsx), so the
        // grid/modal show each field once while the save still sends every record.
        const isType = (rec: PartnerField, t: string) => (rec.applicationType ?? '').toLowerCase() === t.toLowerCase();
        const buildTypeConfig = (records: PartnerField[], type: string): PartnerField[] =>
            (records || [])
                .filter((f) => f && isType(f, type))
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        try {
            const response: any = await GetAllFieldsForPartner(token, partnerId);
            const all: PartnerField[] = Array.isArray(response) ? response : [];
            // ENG-877: Log so we can verify the full GET response in DevTools.
            console.log('[GetAllFieldsForPartner] response:', all);
            const dashboardOnly = buildTypeConfig(all, 'Dashboard');
            setPartnerFieldsConfig(dashboardOnly);
            const reportOnly = buildTypeConfig(all, 'Report');
            console.log('[GetAllFieldsForPartner] dashboard count:', dashboardOnly.length, 'report count:', reportOnly.length);
            setPartnerReportFieldsConfig(reportOnly);
        } catch (error: any) {
            if (error?.status === 401) {
                try {
                    await retryAfterReauthentication(token, partnerId, async (t: any, p: any) => {
                        const resp: any = await GetAllFieldsForPartner(t, p);
                        const allRetry: PartnerField[] = Array.isArray(resp) ? resp : [];
                        const dashboardOnlyRetry = buildTypeConfig(allRetry, 'Dashboard');
                        setPartnerFieldsConfig(dashboardOnlyRetry);
                        const reportOnlyRetry = buildTypeConfig(allRetry, 'Report');
                        setPartnerReportFieldsConfig(reportOnlyRetry);
                    });
                } catch (reAuthError) {
                    handleLogout();
                }
            } else {
                console.error('Error fetching partner field config:', error);
            }
        } finally {
            fetchingPartnerFieldsRef.current = null;
        }
    };

    // ENG-877: Re-fetch the partner field config whenever the selected
    // partner changes (initial load, user switch, password-reset auto-login).
    useEffect(() => {
        if (selectedPartnerId) {
            fetchPartnerFieldsConfig(selectedPartnerId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPartnerId]);


    const loadPDFData = async (token: string | null, id: string, isDetails: boolean = true) => {
        const pdfData: any = await GetPDFData(token, id, isDetails);
        if (!pdfData || !pdfData.fileName || !pdfData.bits || !pdfData.contentType) {
            throw new Error('Invalid PDF data');
        }
        const pdfBlob = base64ToBlob(pdfData.bits, pdfData.contentType);
        FileSaver.saveAs(pdfBlob, pdfData.fileName);
    };

    const handleLoadPDF = async (id: string, isDetails: boolean = true) => {
        setPDFDownloading(true);
        const storedUser: any = localStorage.getItem('accessToken');
        const token = JSON.parse(storedUser);
        try {
            await loadPDFData(token, id, isDetails);
            setPDFDownloading(false);
        } catch (error: any) {
            console.error('Error fetching data:', error);
            setPDFDownloading(false);
            if (error.status === 401) {
                try {
                    await retryAfterReauthentication(token, id, loadPDFData);
                } catch (reAuthError) {
                    handleLogout();
                }
            }
        }
    };

    const base64ToBlob = (base64Data: string, contentType: string) => {
        const byteCharacters = atob(base64Data);
        const byteArrays = [];
        for (let offset = 0; offset < byteCharacters.length; offset += 512) {
            const slice = byteCharacters.slice(offset, offset + 512);
            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
        }
        const blob = new Blob(byteArrays, { type: contentType });
        return blob;
    };

    useEffect(() => {
        const token = localStorage.getItem('accessToken');
        const refreshToken = localStorage.getItem('refreshToken');

        const fetchUserDataAndDashboard = async (token: any, hasRetried: boolean = false) => {
            setLoading(true);
            setTodaysSummaryLoading(true);
            setSummaryLoading(true);
            setReferringLoading(true);
            try {
                const userData = await FetchUserData(token);
                setUser(userData);
                // ─── FIX: Sort mappings alphabetically so default matches Header dropdown ───
                const sortedMappings = [...userData.partnerMappings].sort((a, b) => a.partnerName.localeCompare(b.partnerName));
                const defaultPartnerId = sortedMappings[0].partnerId;
                const defaultPartnerName = sortedMappings[0].partnerName;
                const storedPartnerId: any = localStorage.getItem('partnerId');
                const storedPartnerName: any = localStorage.getItem('partnerName');

                // ─── FIX 4: Write partnerId into dashboardState as a full object, not scalar ───
                // Previously: localStorage.setItem('dashboardState', JSON.stringify(parsedDashboardState.partnerId = storedPartnerId));
                // That mutated parsedDashboardState and stored only the partnerId string value.
                // Fix: merge partnerId into the full object before saving.
                const updatedState = { ...parsedDashboardState, partnerId: storedPartnerId ?? defaultPartnerId };
                localStorage.setItem('dashboardState', JSON.stringify(updatedState));
                localStorage.setItem('reportDataState', JSON.stringify({ ...parsedReportDashboardState, locations: ['All Locations'] }));
                // ─── END FIX 4 ───────────────────────────────────────────────────────────────────

                if (storedPartnerId) {
                    setSelectedPartnerId(storedPartnerId);
                    setSelectedPartnerName(storedPartnerName);
                    // ─── FIX: false = page refresh, preserve stored stats filter dates ────
                    handlePartnerChange(storedPartnerId, storedPartnerName, false);
                } else {
                    // First ever login — treat as real switch to set clean defaults
                    handlePartnerChange(defaultPartnerId, defaultPartnerName, true);
                }

                updateCurrentRequestBody({ ...parsedDashboardState, partnerId: storedPartnerId ? storedPartnerId : defaultPartnerId, skip: parsedDashboardState.skip ?? 0 });
            } catch (error: any) {
                console.error('Error fetching user data:', error);
                // ENG-1197: A 403 means the user is authenticated but lacks
                // dashboard access. Re-auth would succeed and we'd retry forever
                // (thousands of GetDashboardUser calls). Only re-auth on a 401
                // (expired token), and never retry more than once.
                if (error?.status === 403) {
                    setLoading(false);
                    setServerError('You do not have access to the dashboard. Please contact your administrator.');
                    handleLogout();
                    return;
                }
                if (error?.status !== 401 || hasRetried) {
                    setLoading(false);
                    handleLogout();
                    return;
                }
                try {
                    const refreshToken = localStorage.getItem('refreshToken');
                    if (!refreshToken) {
                        handleLogout();
                        return;
                    }
                    const reAuthResponse = await ReAuthenticate(token, refreshToken);
                    localStorage.setItem('accessToken', JSON.stringify(reAuthResponse.accessToken));
                    await fetchUserDataAndDashboard(reAuthResponse.accessToken, true);
                } catch (reAuthError) {
                    setLoading(false);
                    handleLogout();
                    return;
                }
            } finally {
                setLoading(false);
            }
        };

        if (token && refreshToken) {
            setLoading(true);
            setLoggedIn(true);
            const tokenParsed = JSON.parse(token);
            fetchUserDataAndDashboard(tokenParsed);
        } else {
            handleLogout();
            setLoggedIn(false);
        }
    }, []);

    const loginUser = async (username: string, password: string) => {
        try {
            const response = await AuthenticateUser(username, password);
            const authResponse = await response;
            const loginToken = authResponse.accessToken;
            const refreshToken = authResponse.refreshToken;
            setServerError('');

            if (loginToken && refreshToken) {
                localStorage.setItem('accessToken', JSON.stringify(loginToken));
                localStorage.setItem('refreshToken', JSON.stringify(refreshToken));
                return loginToken;
            } else {
                throw new Error('Invalid tokens');
            }
        } catch (error: any) {
            setServerError(`Failed to login: ${error.message}`);
            setLoading(false);
        }
    };

    const fetchUserData = async (token: string) => {
        try {
            const userData = await FetchUserData(token);
            return userData;
        } catch (error) {
            throw new Error('Failed to fetch user data');
        }
    };

    const handleLogin = async (username: string, password: string) => {
        setLoading(true);
        let token;

        try {
            token = await loginUser(username, password);
        } catch (error) {
            setLoading(false);
        }
        if (token) {
            try {
                const userData = await fetchUserData(token);
                setError('');
                setUser(userData);
                if (userData.partnerMappings.length > 0) {
                    // ─── FIX: Use alphabetically sorted first partner, matching Header dropdown ──
                    const sortedMappings = [...userData.partnerMappings].sort((a, b) => a.partnerName.localeCompare(b.partnerName));
                    const defaultPartner = sortedMappings[0];
                    // Fresh login — always reset dates cleanly
                    handlePartnerChange(defaultPartner.partnerId, defaultPartner.partnerName, true);
                    updateCurrentRequestBody({ ...currentRequestBody, partnerId: defaultPartner.partnerId });
                    setSelectedPartnerId(defaultPartner.partnerId);
                    setSelectedPartnerName(defaultPartner.partnerName);
                    // ENG fix: fetch the partner field config explicitly on login.
                    // The [selectedPartnerId] effect can miss on login (no reference
                    // change / timing), which left the Configure Fields modals empty
                    // and the grid columns wrong until a manual page refresh.
                    fetchPartnerFieldsConfig(defaultPartner.partnerId);
                    setLoggedIn(true);
                    setCurrentPage(0);
                    setLoading(false);
                    setChatData(null);
                }
            } catch (error: any) {
                setError('An error occurred during login. Please try again later.');
                setLoading(false);
                if (error.status === 401) {
                    try {
                        const refreshToken: string | null = localStorage.getItem('refreshToken');
                        if (!refreshToken) {
                            throw new Error('Refresh token not found');
                        }
                        const reAuthResponse = await ReAuthenticate(token, refreshToken);
                        localStorage.setItem('accessToken', JSON.stringify(reAuthResponse.accessToken));
                        await handleLogin(username, password);
                    } catch (reAuthError) {
                        console.error('Error reauthenticating:', reAuthError);
                        handleLogout();
                    }
                }
            }
        }
    };

    const handleLogout = async () => {
        const accessToken: any = localStorage.getItem('accessToken');
        const refreshToken: any = localStorage.getItem('refreshToken');
        if (accessToken !== null && refreshToken !== null) {
            try {
                setLoading(true);
                await LogOut(accessToken, refreshToken);
            } catch (error) {
                setLoading(false);
                throw new Error('Failed to logout');
            } finally {
                setLoggedIn(false);
                setUser(null);
                setSelectedPartnerId('');
                setSelectedPartnerName('');
                setFirstPartnerId('');
                setCurrentPage(0);
                setTodaysSummary({
                    newVisitorsToday: 0,
                    repeatVisitorsToday: 0,
                    totalVeraChatsToday: 0,
                    requestedLiveChatToday: 0
                });
                setSummary({
                    newVisitorsInDateRange: 0,
                    repeatVisitorsInDateRange: 0,
                    totalVeraChatsInDateRange: 0,
                    requestedLiveChatInDateRange: 0
                });
                setReferringUrls([]);
                setDashboardData([]);
                setChatData(null);
                setCurrentRequestBody({
                    skip: 0,
                    take: 10,
                    sorts: [{ sort: 'CreatedOn', isAscending: true }],
                    partnerId: '',
                    customStartDate: fromDate,
                    customEndDate: toDate,
                    searchText: '',
                    isDateFilter: true,
                    dateFilter: 'ThisMonth',
                    chatTypes: ['All'],
                    locations: ['All Locations'],
                    isChatType: true,
                });
                setSelectedCheckboxData(['']);
                setChatData(null);
                setLoading(false);
                localStorage.clear();
            }
        }
    };

    const convertDateToAPIFormat = (dateString: string) => {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}T00:00:00.000Z`;
    };

    // ─── FIX: handleLoadCountData no longer blindly overwrites storedSummaryData ──────────
    // Previously it always wrote a fresh object, replacing the user's saved dateFilter
    // with whatever was passed in (always the reset default on refresh).
    // Fix: only write when there is no existing entry or the partner changed.
    // On same-partner calls, preserve the existing stored dates and just update partnerId.
    const handleLoadCountData = async (partnerId: string | undefined, dateFilter: string, startDate: string, endDate: string) => {
        const existing = getSummaryState();
        const shouldPreserve = existing.partnerId === partnerId && existing.dateFilter;

        if (!shouldPreserve) {
            localStorage.setItem('storedSummaryData', JSON.stringify({
                customStartDate: startDate,
                customEndDate: endDate,
                partnerId,
                dateFilter,
            }));
        } else {
            localStorage.setItem('storedSummaryData', JSON.stringify({ ...existing, partnerId }));
        }

        const requestBody = { customStartDate: startDate, customEndDate: endDate, partnerId, dateFilter };
        const token: any = localStorage.getItem('accessToken');
        await loadCountData(token, requestBody);
    };
    // ─── END FIX ─────────────────────────────────────────────────────────────────────────

    // ─── FIX: handleLoadReferringUrlData no longer blindly overwrites storedReferringUrlData ─
    // Same fix as handleLoadCountData above.
    const handleLoadReferringUrlData = async (partnerId: string | undefined, dateFilter: string, startDate: string, endDate: string) => {
        const existing = getReferringState();
        const shouldPreserve = existing.partnerId === partnerId && existing.dateFilter;

        if (!shouldPreserve) {
            localStorage.setItem('storedReferringUrlData', JSON.stringify({
                customStartDate: startDate,
                customEndDate: endDate,
                partnerId,
                dateFilter,
            }));
        } else {
            localStorage.setItem('storedReferringUrlData', JSON.stringify({ ...existing, partnerId }));
        }

        const requestBody = { customStartDate: startDate, customEndDate: endDate, partnerId, dateFilter };
        const token: any = localStorage.getItem('accessToken');
        await loadReferringUrlData(token, requestBody);
    };
    // ─── END FIX ─────────────────────────────────────────────────────────────────────────

    const loadCountData = async (token: string | null, requestBody: any) => {
        try {
            const countData: any = await GetDashboardCounts(token, requestBody);
            const stats = [
                { label: 'Repeat Visitors', value: countData.repeatVisitorsToday },
                { label: 'Total Vera Chats', value: countData.totalVeraChatsToday },
                { label: 'Total All Chats', value: countData.requestedLiveChatToday }
            ];
            const statsRange = [
                { label: 'Repeat Visitors', value: countData.repeatVisitorsInDateRange },
                { label: 'Total Vera Chats', value: countData.totalVeraChatsInDateRange },
                { label: 'Total All Chats', value: countData.requestedLiveChatInDateRange }
            ];
            setTodaysSummary(stats);
            setSummary(statsRange);
            setSummaryLoading(false);
            setTodaysSummaryLoading(false);
        } catch (error: any) {
            setTodaysSummary([]);
            setSummary([]);
            setSummaryLoading(false);
            setTodaysSummaryLoading(false);
            if (error.status === 401) {
                try {
                    await retryAfterReauthentication(token, requestBody, loadCountData);
                } catch (reAuthError) {
                    setSummaryLoading(false);
                    setTodaysSummaryLoading(false);
                    handleLogout();
                    return;
                }
            }
        }
    };

    const loadReferringUrlData = async (token: string | null, requestBody: any) => {
        try {
            const urlRange: any[] = await GetDashboardReferringUrl(token, requestBody);
            setReferringUrls(urlRange);
            setReferringLoading(false);
        } catch (error: any) {
            setReferringUrls([]);
            setReferringLoading(false);
            if (error.status === 401) {
                try {
                    await retryAfterReauthentication(token, requestBody, loadReferringUrlData);
                } catch (reAuthError) {
                    setReferringLoading(false);
                    handleLogout();
                    return;
                }
            }
        }
    };

    const retryAfterReauthentication = async (token: any | null, requestBody: any, fetchDataAndUpdateUI: Function) => {
        const refreshToken: string | null = localStorage.getItem('refreshToken');
        if (!refreshToken) {
            throw new Error('Refresh token not found');
        }
        const reAuthResponse = await ReAuthenticate(token, refreshToken);
        const newToken = reAuthResponse.accessToken;
        await fetchDataAndUpdateUI(newToken, requestBody);
    };

    const handleChangePage = async (event: unknown, newPage: number) => {
        setCurrentPage(newPage);
        setPage(newPage);
        updateCurrentRequestBody({ ...currentRequestBody, skip: newPage * 10, take: rowsPerPage });
        fetchDashboardData(selectedPartnerId, { ...currentRequestBody, skip: newPage * 10 }, newPage * 10, rowsPerPage);
    };

    const handleUsernameChange = (newUsername: string) => {
        setUsername(newUsername);
        validateInputs(newUsername, password);
    };

    const handlePasswordChange = (newPassword: string) => {
        setPassword(newPassword);
        validateInputs(username, newPassword);
    };

    const validateInputs = (newUsername: string, newPassword: string) => {
        if (!newUsername && !newPassword) {
            setError('Please enter both username and password.');
        } else if (!newUsername) {
            setError('Please enter your username.');
        } else if (!newPassword) {
            setError('Please enter your password.');
        } else {
            setError('');
        }
    };

    const handleGridFilterSubmit = async (filterData: any) => {
        const dateFilter = determineDateType(filterData.selectedRadioValue);
        let chatType;
        if (filterData.selectedChatTypeValues != undefined) {
            chatType = determineChatType(filterData.selectedChatTypeValues);
        } else {
            chatType = determineChatType(filterData.prevSelectedChatTypeValues);
        }
        let locations;
        if (filterData.selectedLocationsValues != undefined) {
            locations = filterData.selectedLocationsValues;
        } else {
            locations = filterData.prevSelectedLocationsValues;
        }
        let startD = filterData.fromDate ? new Date(filterData.fromDate) : new Date(currentRequestBody.customStartDate);
        let endD = filterData.toDate ? new Date(filterData.toDate) : new Date(currentRequestBody.customEndDate);
        startD.setDate(startD.getDate() + 1);
        endD.setDate(endD.getDate() + 1);

        updateCurrentRequestBody({
            ...currentRequestBody,
            customStartDate: convertDateToAPIFormat(startD.toString()),
            customEndDate: convertDateToAPIFormat(endD.toString()),
            isDateFilter: true,
            dateFilter: dateFilter,
            chatTypes: chatType,
            locations: locations,
            partnerId: selectedPartnerId,
            skip: 0
        });

        fetchDashboardData(
            selectedPartnerId,
            {
                ...currentRequestBody,
                customStartDate: convertDateToAPIFormat(startD.toString()),
                customEndDate: convertDateToAPIFormat(endD.toString()),
                isDateFilter: true,
                dateFilter: dateFilter,
                chatTypes: chatType,
                locations: locations,
                skip: 0
            }, 0, rowsPerPage);
        // Reset to page 0 — user explicitly changed a filter
        setPage(0);
        setCurrentPage(0);
    };

    enum DateFilter {
        Custom = 'Custom',
        Today = 'Today',
        ThisWeek = 'ThisWeek',
        LastWeek = 'LastWeek',
        ThisMonth = 'ThisMonth',
        LastMonth = 'LastMonth',
        ThisYear = 'ThisYear',
        All = 'All'
    }

    const determineDateType = (selectedDateFilter: string): DateFilter => {
        let dateType: DateFilter = DateFilter.ThisMonth;

        switch (selectedDateFilter) {
            case 'Today':
                dateType = DateFilter.Today;
                break;
            case 'This Week':
                dateType = DateFilter.ThisWeek;
                break;
            case 'Month-to-Date':
                dateType = DateFilter.ThisMonth;
                break;
            case 'Last Month':
                dateType = DateFilter.LastMonth;
                break;
            case 'Year-to-Date':
                dateType = DateFilter.ThisYear;
                break;
            case 'All Dates':
                dateType = DateFilter.All;
                break;
            case 'Custom':
                dateType = DateFilter.Custom;
                break;
            case 'Custom date range':
                dateType = DateFilter.Custom;
                break;
            case undefined:
                dateType = DateFilter.ThisMonth;
                break;
        }
        return dateType;
    };

    const determineChatType = (selectedChatTypeValues: string[]) => {
        const isChatTypeSelected = selectedChatTypeValues.includes('Chat Type');
        const filteredChatTypes = selectedChatTypeValues.filter(value => value.trim() !== '');
        if (isChatTypeSelected && filteredChatTypes.length === 1) {
            return ['All'];
        }
        const result = filteredChatTypes.filter(value => value !== 'Chat Type');
        if (!isChatTypeSelected) {
            setSelectedCheckboxData(result);
        }
        return result;
    };

    const handleSearchRequest = async (searchTerm: any) => {
        updateCurrentRequestBody({ ...currentRequestBody, searchText: searchTerm, skip: 0 });
        fetchDashboardData(selectedPartnerId, { ...currentRequestBody, searchText: searchTerm, skip: 0 }, 0, rowsPerPage);
        // Reset to page 0 — user explicitly searched
        setPage(0);
        setCurrentPage(0);
    };

    const handleSortRequest = async (sortTerm: any) => {
        // ─── FIX 5: Replaced broken field-by-field localStorage write for sorts ──────────
        // Previously: localStorage.setItem('dashboardState', JSON.stringify(parsedDashboardState.sorts[0].isAscending = ...))
        // That mutated parsedDashboardState.sorts[0] and stored only the boolean value.
        // Fix: read current sort direction from state, build a new object, write it whole.
        const currentIsAscending = parsedDashboardState?.sorts?.[0]?.isAscending ?? true;
        const newIsAscending = !currentIsAscending;
        const newSorts = [{ sort: sortTerm.field, isAscending: newIsAscending }];
        updateCurrentRequestBody({ ...currentRequestBody, partnerId: selectedPartnerId, sorts: newSorts, skip: 0 });
        await fetchDashboardData(selectedPartnerId, { ...currentRequestBody, sorts: newSorts, skip: 0 }, 0, rowsPerPage);
        // Reset to page 0 — user explicitly changed sort
        setPage(0);
        setCurrentPage(0);
        // ─── END FIX 5 ───────────────────────────────────────────────────────────────────
    };

    const fetchReportsData = async (token: string | null, requestBody: any, setServerError: any) => {
        const CSVData: any = await GetDashboardReports(token, requestBody, setServerError);
        if (!CSVData || !CSVData.fileName || !CSVData.bits || !CSVData.contentType) {
            throw new Error('Invalid CSV data');
        }
        const pdfBlob = base64ToBlob(CSVData.bits, CSVData.contentType);
        FileSaver.saveAs(pdfBlob, CSVData.fileName);
    };

    const downloadReferringUrlCSV = async (filterData: any) => {
        const csvHeader = 'URL,COUNT';
        const csvRows = referringUrls.map(({ url, count }) => `${url},${count}`);
        const csvString = [csvHeader, ...csvRows].join('\n');
        const blob = new Blob([csvString], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const downloadLink = document.createElement('a');
        downloadLink.href = url;
        let ReferringLinkName = '';
        if (filterData.selectedRadioValue === 'Custom date range') {
            ReferringLinkName = `${selectedPartnerName}_Referring_urls_From_${filterData.fromD}_To_${filterData.toD}.csv`;
        } else {
            ReferringLinkName = `${selectedPartnerName}_Referring_urls_${filterData.selectedRadioValue}.csv`
        }
        downloadLink.download = ReferringLinkName;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        window.URL.revokeObjectURL(url);
    };

    const retryAfterReauthenticationForReports = async (token: any | null, requestBody: any, setServerError: Function) => {
        const refreshToken: string | null = localStorage.getItem('refreshToken');
        if (!refreshToken) {
            throw new Error('Refresh token not found');
        }
        const reAuthResponse = await ReAuthenticate(token, refreshToken);
        const newToken = reAuthResponse.accessToken;
        await fetchReportsData(newToken, requestBody, setServerError);
    };

    const handleReportFilterSubmit = async (filterData: any) => {
        setModalLoading(true);
        setServerError('');
        const dateType = determineDateType(filterData.date);
        const chatType = determineChatType(filterData.selectedReportChatTypeValues);
        const locations = filterData.selectedReportLocationValues;
        let startD: any = "";
        let endD: any = "";
        if (dateType === 'Custom') {
            startD = filterData.fromDateReport ? new Date(filterData.fromDateReport) : new Date('');
            endD = filterData.toDateReport ? new Date(filterData.toDateReport) : new Date('');
            convertDateToAPIFormat(startD.setDate(startD.getDate()).toString());
            convertDateToAPIFormat(endD.setDate(endD.getDate()).toString());
        } else {
            startD = new Date('2024-04-15T17:49:22.525Z');
            endD = new Date('2024-04-15T17:49:22.525Z');
        }

        const requestBody = {
            dateFilter: dateType,
            customStartDate: startD,
            customEndDate: endD,
            reportName: "DashboardReport",
            email: "",
            when: "Now",
            includeUpdates: true,
            includeAnonymized: true,
            sourceIds: [selectedPartnerId],
            promotionIds: ["string"],
            set: "Normal",
            segmentId: 0,
            practiceLocationId: 0,
            includePosts: true,
            excludeNoNetCharge: true,
            activeFilter: true,
            autoRenewFilter: true,
            archiveFilter: true,
            includeIncompleteStatuses: true,
            isMarkSubmitted: true,
            welcomeKitFilter: true,
            lostPetFilter: true,
            foundPetFilter: true,
            recoveredPetFilter: true,
            chatTypes: chatType,
            locations: locations
        };

        localStorage.setItem('reportDataState', JSON.stringify(requestBody));

        const storedUser: any = localStorage.getItem('accessToken');
        const token = JSON.parse(storedUser);
        try {
            await fetchReportsData(token, requestBody, setServerError);
            setModalLoading(false);
        } catch (error: any) {
            if (error.message === 'Failed to fetch') {
                try {
                    await retryAfterReauthenticationForReports(token, requestBody, setServerError);
                } catch (reAuthError: any) {
                    if (reAuthError.message === 'Failed to fetch') {
                        handleLogout();
                        return;
                    } else {
                        setServerError(`Error fetching CSV data.`);
                    }
                }
            } else {
                setServerError(`Error fetching CSV data.`);
            }
        } finally {
            setModalLoading(false);
        }
    };

    const handleDateFilterSummarySubmit = async (filterData: any) => {
        let fromDate: string;
        let toDate: string;

        setSummaryLoading(true);

        switch (filterData.selectedRadioValue) {
            case 'All Dates':
                const currentAllDate = new Date();
                const oneAllYearAgo = new Date(currentAllDate);
                oneAllYearAgo.setFullYear(oneAllYearAgo.getFullYear() - 10);
                fromDate = oneAllYearAgo.toISOString();
                toDate = currentAllDate.toISOString();
                handleLoadCountData(selectedPartnerId, 'All', fromDate, toDate);
                break;
            case 'Today':
                const currentDayStart = new Date();
                currentDayStart.setDate(currentDayStart.getDate() - currentDayStart.getDay());
                const currentDayEnd = new Date();
                currentDayEnd.setDate(currentDayEnd.getDate() - currentDayEnd.getDay() + 6);
                fromDate = currentDayStart.toISOString();
                toDate = currentDayEnd.toISOString();
                handleLoadCountData(selectedPartnerId, 'Today', fromDate, toDate);
                break;
            case 'This Week':
                const currentWeekStart = new Date();
                currentWeekStart.setDate(currentWeekStart.getDate() - currentWeekStart.getDay());
                const currentWeekEnd = new Date();
                currentWeekEnd.setDate(currentWeekEnd.getDate() - currentWeekEnd.getDay() + 6);
                fromDate = currentWeekStart.toISOString();
                toDate = currentWeekEnd.toISOString();
                handleLoadCountData(selectedPartnerId, 'ThisWeek', fromDate, toDate);
                break;
            case 'Month-to-Date':
                const currentMonth = new Date();
                const lastMonthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, currentMonth.getDate());
                fromDate = lastMonthStart.toISOString();
                toDate = currentMonth.toISOString();
                handleLoadCountData(selectedPartnerId, 'ThisMonth', fromDate, toDate);
                break;
            case 'Last Month':
                const currentLastMonth = new Date();
                const lastLastMonthStart = new Date(currentLastMonth.getFullYear(), currentLastMonth.getMonth() - 1, currentLastMonth.getDate());
                fromDate = lastLastMonthStart.toISOString();
                toDate = currentLastMonth.toISOString();
                handleLoadCountData(selectedPartnerId, 'LastMonth', fromDate, toDate);
                break;
            case 'Year-to-Date':
                const currentDate = new Date();
                const oneYearAgo = new Date(currentDate);
                oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
                fromDate = oneYearAgo.toISOString();
                toDate = currentDate.toISOString();
                handleLoadCountData(selectedPartnerId, 'ThisYear', fromDate, toDate);
                break;
            default:
                const fromDateWithoutTime = new Date(filterData.fromDate);
                fromDateWithoutTime.setDate(fromDateWithoutTime.getDate());
                fromDate = fromDateWithoutTime.toISOString();
                const toDateWithoutTime = new Date(filterData.toDate);
                toDateWithoutTime.setDate(toDateWithoutTime.getDate());
                toDate = toDateWithoutTime.toISOString();
                handleLoadCountData(selectedPartnerId, 'Custom', fromDate, toDate);
                break;
        }
    };

    const handleDateFilterReferringURLSubmit = async (filterData: any) => {
        let fromDate: string;
        let toDate: string;

        setReferringLoading(true);

        switch (filterData.selectedRadioValue) {
            case 'All Dates':
                const currentAllDate = new Date();
                const oneAllYearAgo = new Date(currentAllDate);
                oneAllYearAgo.setFullYear(oneAllYearAgo.getFullYear() - 10);
                fromDate = oneAllYearAgo.toISOString();
                toDate = currentAllDate.toISOString();
                handleLoadReferringUrlData(selectedPartnerId, 'All', fromDate, toDate);
                break;
            case 'Today':
                const currentDayStart = new Date();
                currentDayStart.setDate(currentDayStart.getDate() - currentDayStart.getDay());
                const currentDayEnd = new Date();
                currentDayEnd.setDate(currentDayEnd.getDate() - currentDayEnd.getDay() + 6);
                fromDate = currentDayStart.toISOString();
                toDate = currentDayEnd.toISOString();
                handleLoadReferringUrlData(selectedPartnerId, 'Today', fromDate, toDate);
                break;
            case 'This Week':
                const currentWeekStart = new Date();
                currentWeekStart.setDate(currentWeekStart.getDate() - currentWeekStart.getDay());
                const currentWeekEnd = new Date();
                currentWeekEnd.setDate(currentWeekEnd.getDate() - currentWeekEnd.getDay() + 6);
                fromDate = currentWeekStart.toISOString();
                toDate = currentWeekEnd.toISOString();
                handleLoadReferringUrlData(selectedPartnerId, 'ThisWeek', fromDate, toDate);
                break;
            case 'Last Month':
                const currentLastMonth = new Date();
                const lastLastMonthStart = new Date(currentLastMonth.getFullYear(), currentLastMonth.getMonth() - 1, currentLastMonth.getDate());
                fromDate = lastLastMonthStart.toISOString();
                toDate = currentLastMonth.toISOString();
                handleLoadCountData(selectedPartnerId, 'LastMonth', fromDate, toDate);
                break;
            case 'Month-to-Date':
                const currentMonth = new Date();
                const lastMonthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, currentMonth.getDate());
                fromDate = lastMonthStart.toISOString();
                toDate = currentMonth.toISOString();
                handleLoadReferringUrlData(selectedPartnerId, 'ThisMonth', fromDate, toDate);
                break;
            case 'Year-to-Date':
                const currentDate = new Date();
                const oneYearAgo = new Date(currentDate);
                oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
                fromDate = oneYearAgo.toISOString();
                toDate = currentDate.toISOString();
                handleLoadReferringUrlData(selectedPartnerId, 'ThisYear', fromDate, toDate);
                break;
            default:
                const fromDateWithoutTime = new Date(filterData.fromDate);
                fromDateWithoutTime.setDate(fromDateWithoutTime.getDate());
                fromDate = fromDateWithoutTime.toISOString();
                const toDateWithoutTime = new Date(filterData.toDate);
                toDateWithoutTime.setDate(toDateWithoutTime.getDate());
                toDate = toDateWithoutTime.toISOString();
                handleLoadReferringUrlData(selectedPartnerId, 'Custom', fromDate, toDate);
                break;
        }
    };

    const handleOpenDialog = () => {
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setServerError(null);
    };

    const cancelResetPassword = () => {
        setServerError('');
        setResetEmail('');
        setResetCode('');
    };

    const openPasswordReset = () => {
        setServerError('');
    }

    const handleSendEmail = async (email: string): Promise<{ message: string | null }> => {
        setResetEmail(email);
        setLoading(true);
        try {
            const sendEmailResponse = await ForgotPassword(email);
            setResetEmailSuccess(true);
            setServerError('');
            setLoading(false);
            return sendEmailResponse;
        } catch (error: any) {
            console.error('Error sending forgot password request:', error);
            setServerError(error);
            setResetEmailSuccess(false);
            setLoading(false);
            return error.message;
        }
    };

    const handleVerifyCode = async (code: string): Promise<{ message: string | null | undefined }> => {
        setLoading(true);
        setResetCode(code);
        if (code !== '' && resetEmail !== '') {
            try {
                const sendCodeResponse = await SendResetCode(resetEmail, code);
                setResetCodeSuccess(true);
                setLoading(false);
                setServerError('');
                return sendCodeResponse;
            } catch (error: any) {
                console.error('Error sending forgot password request:', error);
                setLoading(false);
                setResetCodeSuccess(false);
                setServerError(error);
                return error.message;
            }
        } else {
            setLoading(false);
            setResetCodeSuccess(false);
            setServerError('Please enter your email address first to reset your password.');
            return { message: undefined };
        }
    };

    interface ResetPasswordHandler {
        (password: string): Promise<{ message: string | null | undefined, code: number | null | undefined, errors: string[] | null | undefined }>;
    }
    const handleResetPassword: ResetPasswordHandler = async (newPassword: string) => {
        setLoading(true);
        if (resetCode !== '' && resetEmail !== '' && newPassword !== '') {
            try {
                const resetResponse = await ResetPassword(resetEmail, resetCode, newPassword);
                if (resetResponse.accessToken && resetResponse.refreshToken) {
                    localStorage.setItem('accessToken', JSON.stringify(resetResponse.accessToken));
                    localStorage.setItem('refreshToken', JSON.stringify(resetResponse.refreshToken));
                    setLoggedIn(true);
                    try {
                        const userData = await fetchUserData(resetResponse.accessToken);
                        setError('');
                        setUser(userData);
                        // ─── FIX: Use alphabetically sorted first partner ──────────────────────────
                        const sortedMappings = [...userData.partnerMappings].sort((a, b) => a.partnerName.localeCompare(b.partnerName));
                        const defaultPartner = sortedMappings[0];
                        handlePartnerChange(defaultPartner.partnerId, defaultPartner.partnerName, true);
                        updateCurrentRequestBody({ ...currentRequestBody, partnerId: defaultPartner.partnerId });
                        setSelectedPartnerId(defaultPartner.partnerId);
                        setSelectedPartnerName(defaultPartner.partnerName);
                        // ENG fix: fetch the partner field config explicitly on
                        // password-reset auto-login too (see handleLogin note).
                        fetchPartnerFieldsConfig(defaultPartner.partnerId);
                        setCurrentPage(0);
                        setChatData(null);
                        handleLoadCountData(defaultPartner.partnerId, 'ThisMonth', fromDate, toDate);
                        handleLoadReferringUrlData(defaultPartner.partnerId, 'ThisMonth', fromDate, toDate);
                        fetchDashboardData(defaultPartner.partnerId, { ...currentRequestBody, partnerId: defaultPartner.partnerId, skip: 0 }, 0, rowsPerPage);
                        setLoading(false);
                        return resetResponse;
                    } catch (error: any) {
                        setError('An error occurred during login. Please try again later.');
                        setLoading(false);
                        return error.message;
                    }
                } else {
                    setServerError('Error: Access token or refresh token missing in reset response');
                    setLoading(false);
                }
            } catch (error: any) {
                setServerError(error);
                setLoading(false);
                return error;
            }
        } else {
            setLoading(false);
            setResetCodeSuccess(false);
            setServerError('Please enter your email address first to reset your password.');
            return { message: undefined };
        }
    };

    let formattedPartnerName = selectedPartnerName.replace(/ /g, '_').toLowerCase();

    return (
        <Router>
            <div className={classes.appContainer}>
                <PageHeader
                    onLogout={handleLogout}
                    isLoggedIn={isLoggedIn}
                    onOpenReportDialog={handleOpenDialog}
                    userData={user}
                    onPartnerChange={(partnerId: any, partnerName: string) => handlePartnerChange(partnerId, partnerName, true)}
                />
                {loading ? (
                    <Paper elevation={0} className="pageLoaderContainer">
                        <Typography className="pageLoaderImage">
                            <span className="loaderCircleSmall"></span>
                            <span className="loaderCircleBig"></span>
                        </Typography>
                        <Typography className="pageLoaderText">PROCESSING...</Typography>
                    </Paper>
                ) : (
                    <Suspense
                        fallback={
                            <Paper elevation={0} className="pageLoaderContainer">
                                <Typography className="pageLoaderImage">
                                    <span className="loaderCircleSmall"></span>
                                    <span className="loaderCircleBig"></span>
                                </Typography>
                                <Typography className="pageLoaderText">PROCESSING...</Typography>
                            </Paper>
                        }
                    >
                        <Routes>
                            {isLoggedIn || localStorage.getItem('accessToken') !== null ? (
                                <>
                                    <Route
                                        path="/login"
                                        element={<Navigate replace to={`/${formattedPartnerName}`} />}
                                    />
                                    <Route
                                        path={`/${formattedPartnerName}`}
                                        element={
                                            <>
                                                <div className="statsContainer">
                                                    <StatsCard
                                                        cardType="circle"
                                                        visibility="visible"
                                                        storedData={storedSummaryState}
                                                        isSummaryLoading={summaryLoading}
                                                        title="Summary"
                                                        stats={summary}
                                                        onSubmitDateFilter={handleDateFilterSummarySubmit}
                                                    />
                                                    <StatsCard
                                                        cardType="bar"
                                                        visibility="visible"
                                                        storedData={storedReferringState}
                                                        isReferringLoading={referringUrlLoading}
                                                        title="Referring Url"
                                                        stats={referringUrls.slice(0, 10)}
                                                        onSubmitDateFilter={handleDateFilterReferringURLSubmit}
                                                        downloadCSVClick={downloadReferringUrlCSV}
                                                    />
                                                </div>
                                                <GridComponent
                                                    data={dashboardData}
                                                    locationsData={locationsCheckboxList}
                                                    chatData={chatData}
                                                    isDialogOpen={dialogOpen}
                                                    onCloseDialog={handleCloseDialog}
                                                    onSubmitGridFilter={handleGridFilterSubmit}
                                                    onSubmitReportFilter={handleReportFilterSubmit}
                                                    onSearchRequest={handleSearchRequest}
                                                    onSort={handleSortRequest}
                                                    dataLoading={loading}
                                                    isLoading={searchLoading}
                                                    isModalLoading={modalLoading}
                                                    page={page}
                                                    currentPage={currentPage}
                                                    rowsPerPage={rowsPerPage}
                                                    totalCount={totalCount}
                                                    handleChangePage={handleChangePage}
                                                    handlePDFClick={handleLoadPDF}
                                                    isPDFDownloading={pdfDownloading}
                                                    handleGetChatData={handleLoadChatData}
                                                    onSaveFieldConfig={handleSaveFieldConfig}
                                                    onSaveReportFieldConfig={handleSaveReportFieldConfig}
                                                    partnerFieldsConfig={partnerFieldsConfig}
                                                    partnerReportFieldsConfig={partnerReportFieldsConfig}
                                                    selectedCheckboxVal={selectedCheckboxData}
                                                    serverError={serverError}
                                                    isPartnerAdmin={!!user?.partnerMappings?.find((p) => p.partnerId === selectedPartnerId)?.isPartnerAdmin}
                                                />
                                            </>
                                        }
                                    />
                                    <Route
                                        path="*"
                                        element={<Navigate replace to={`/${formattedPartnerName}`} />}
                                    />
                                </>
                            ) : (
                                <>
                                    <Route
                                        path="/login"
                                        element={
                                            <Login
                                                onLogin={handleLogin}
                                                error={error}
                                                serverError={serverError}
                                                onUsernameChange={handleUsernameChange}
                                                onPasswordChange={handlePasswordChange}
                                                handleForgotPasswordNavigate={openPasswordReset}
                                            />
                                        }
                                    />
                                    <Route
                                        path="/forgot-password"
                                        element={
                                            <SendEmailPage
                                                handleSendEmail={handleSendEmail}
                                                handleCancelReset={cancelResetPassword}
                                                serverError={serverError}
                                                handleSuccess={resetEmailSuccess}
                                            />
                                        }
                                    />
                                    <Route
                                        path="/verify-code"
                                        element={
                                            <VerifyCodePage
                                                handleVerifyCode={handleVerifyCode}
                                                handleCancelReset={cancelResetPassword}
                                                serverError={serverError}
                                                handleSuccess={resetCodeSuccess}
                                            />
                                        }
                                    />
                                    <Route
                                        path="/reset-password"
                                        element={
                                            <ConfirmNewPasswordPage
                                                handleResetPassword={handleResetPassword}
                                                handleCancelReset={cancelResetPassword}
                                                serverError={serverError}
                                            />
                                        }
                                    />
                                    <Route path="*" element={<Navigate replace to="/login" />} />
                                </>
                            )}
                        </Routes>
                    </Suspense>
                )}
                <PageFooter />
            </div>
        </Router>
    );

};
export default Dashboard;
